import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, Container, Divider, IconButton, Switch, useMediaQuery, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCurrentUser } from "api/services/useCurrentUser";
import { useDetector } from "api/services/useDetector";
import { Camera, DemoAutoTriggerIcon } from "assets/icons";
import { UserLimitModal } from "components/account/UserLimitModal/UserLimitModal";
import { ExpirePlanBar } from "components/common/ExpirePlanBar/ExpirePlanBar";
import { TerminationNoticeBar } from "components/common/TerminationNoticeBar/TerminationNoticeBar";
import { DEFAULT_EXPLORE_PAGE_PREDICTOR_CONFIG, JANZU_URL } from "config/config";
import { useAppDispatch, useAppSelector } from "hooks/store";
import useExploreSnapShot from "hooks/useCountDown/useExploreSnapShot";
import { useModalLimit } from "hooks/useModalLimit/useModalLimit";
import { useUser } from "hooks/user/useUser";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { ConfirmDeleteModal } from "new-components/modals/ConfirmDeleteModal";
import { Typography } from "new-components/typography/Typography";
import { SimpleDemoStats } from "pages/demo/components/SimpleDemoStats";
import { getUserState, setAuthUser } from "store/slices/Auth/authSlice";
import { Detector } from "types/detectors";
import { dataUrlToByteArray } from "utils/imgtools";

import { DemoPageOnboarding } from "./components/DemoPageOnboarding";
import { DemoSettings } from "./components/DemoSettings";
import { DemoSubNavbar } from "./components/DemoSubNavbar";
import { ResetDemoDetectorModal } from "./components/ResetDemoDetectorModal";
import { SimpleWebcam } from "./components/SimpleWebcam";

const LIMIT_OF_AUTO_CAPTURE_IMAGES = 100;

export interface WebcamRef {
  getScreenshot: () => string | null;
}

export function DemoPage() {
  const { isBlocked, modalLimit, handleOpenModalLimit, handleCloseModalLimit } = useModalLimit("iqIsBlocked");

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const userData = useAppSelector(getUserState);
  const [autoTrigger, setAutoTrigger] = useState(false);
  const [triggerCounter, setTriggerCounter] = useState(0);
  const [triggerInterval, setTriggerInterval] = useState(30);
  const [autoTriggerPopOver, setAutoTriggerPopOver] = useState(false);
  const [exploreDetectorId, setExploreDetectorId] = useState<string>("");
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);
  const [isResetModalOpen, setIsResetModalOpen] = useState<boolean>(false);
  const user = useUser();
  const hasExploreDetector = user.explore_detector !== null;
  const [hasMobileThresholdError, setHasMobileThresholdError] = useState<boolean>(false);

  // Use React Query for detector operations
  const {
    createDetector,
    deleteDetector,
    isLoading: isMutatingDetector,
    detector,
    isLoadingDetector,
  } = useDetector(exploreDetectorId);

  const { refetch: refetchUser } = useCurrentUser();
  const dispatch = useAppDispatch();

  const isSM = useMediaQuery(theme.breakpoints.down("md"));
  const isMD = useMediaQuery(theme.breakpoints.up(770));

  const cameraHeight = isSM ? 260 : 350;
  const [isQueryLimitModalOpen, setIsQueryLimitModalOpen] = useState<boolean>(false);
  const { disabledCountdown, snapDisabled, setSnapDisabled, setDisabledCountdown } = useExploreSnapShot(0);

  const webcamRef = useRef<WebcamRef>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (userData.explore_detector) {
      setExploreDetectorId(userData.explore_detector);
    }
  }, [userData]);

  useEffect(() => {
    if (!deviceId) {
      setSnapDisabled(true);
    } else {
      setSnapDisabled(false);
    }
  }, [deviceId, setSnapDisabled]);

  // webcam snapshot
  const snapshot = useCallback((): Uint8Array | null => {
    if (webcamRef?.current) {
      // disable snapping for 1 second
      setSnapDisabled(true);
      const imageUrl = webcamRef.current.getScreenshot();
      if (imageUrl) {
        return dataUrlToByteArray(imageUrl);
      }
      setSnapDisabled(false);
    }
    return null;
  }, [webcamRef, setSnapDisabled]);

  const handleResponse = useCallback(
    async (response: Response) => {
      // Check for HTTP status 429 (Too Many Requests)
      if (response.status === 429) {
        // Handle the 429 status
        const retryAfter = response.headers.get("Retry-After");
        setDisabledCountdown(retryAfter ? +retryAfter : 0);
        setSnapDisabled(true);
        setIsQueryLimitModalOpen(true);
        return undefined;
      }

      if (!response.ok) {
        // Handle other HTTP errors
        console.error(`Server responded with an error: ${response.status}`);
        setSnapDisabled(false);
        return undefined;
      }

      // Process the successful response
      try {
        const responseData = await response.json();
        setSnapDisabled(false);
        return responseData;
      } catch (error) {
        console.error("Error parsing the response:", error);
        return undefined;
      }
    },
    [setDisabledCountdown, setSnapDisabled, setIsQueryLimitModalOpen]
  );

  const createPosicheck = useCallback(async () => {
    // Check if operation is blocked
    if (isBlocked) {
      handleOpenModalLimit();
      return undefined;
    }

    // Check for the presence of the detector and take a snapshot
    if (detector) {
      const image = snapshot();
      if (image != null) {
        try {
          // Send the snapshot to the server
          const response = await fetch(`${JANZU_URL}device-api/v1/image-queries?detector_id=${detector.id}`, {
            method: "POST",
            headers: {
              "Content-Type": "image/jpeg",
            },
            body: image,
          });

          // Handle the response
          return handleResponse(response);
        } catch (error) {
          console.error("Network error:", error);
          return undefined;
        }
      }
    }

    return undefined;
  }, [isBlocked, detector, handleOpenModalLimit, snapshot, handleResponse]);

  // setup the create posicheck timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    const triggerAutoCapture = (): NodeJS.Timeout =>
      setInterval(() => {
        createPosicheck();
        if (triggerCounter >= LIMIT_OF_AUTO_CAPTURE_IMAGES) {
          setAutoTrigger(false);
          clearInterval(timer);
          setTriggerCounter(0);
        } else {
          setTriggerCounter(triggerCounter + 1);
        }
      }, triggerInterval * 1000);
    if (autoTrigger) {
      timer = triggerAutoCapture();
      return () => clearInterval(timer);
    }
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTrigger, triggerCounter, triggerInterval]);

  // callback for when predictor changes (from the predictor select component)
  const onDetectorSelect = useCallback((detector: Detector) => {
    setExploreDetectorId(detector.id);
  }, []);

  const onResetDetector = async (labelPrompt: string) => {
    // delete the detector and create a new detector with the current query and confidence threshold.
    try {
      const response = await createDetector({
        ...DEFAULT_EXPLORE_PAGE_PREDICTOR_CONFIG,
        label_prompt: labelPrompt,
        is_explore: true,
        confidence_threshold: 0.75,
      });

      if (response?.id) {
        setExploreDetectorId(response.id);

        // refetch user to make sure explore detector is in user info
        const updatedUser = await refetchUser();
        dispatch(setAuthUser(updatedUser));

        enqueueSnackbar("Detector reset successfully", { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar("Failed to reset the detector", { variant: "error" });
    }
    setIsResetModalOpen(false);
  };

  const createNewExploreDetector = async (labelPrompt: string) => {
    // delete the detector and create a new detector with the current query
    try {
      if (detector?.id) {
        await deleteDetector.mutateAsync(detector.id);
      }

      const response = await createDetector({
        ...DEFAULT_EXPLORE_PAGE_PREDICTOR_CONFIG,
        label_prompt: labelPrompt,
        is_explore: true,
        confidence_threshold: 0.75,
      });

      if (response?.id) {
        setExploreDetectorId(response.id);

        // refetch user to make sure explore detector is in user info
        const updatedUser = await refetchUser();
        dispatch(setAuthUser(updatedUser));

        enqueueSnackbar("Detector created successfully", { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar("Failed to create detector", { variant: "error" });
    }
  };

  const autoCaptureSection = () => (
    <Box className="flex justify-between items-center rounded sm:bg-white w-full">
      <Switch
        checked={autoTrigger}
        onChange={(e) => setAutoTrigger(e.target.checked)}
        sx={{ mr: 1 }}
        disabled={detector?.status === "OFF" || !detector}
      />
      <Box className="inline-flex items-center py-1 pr-4">
        <Typography variant="detail" className="text-gray-900 mr-2">
          Auto Capture Every
        </Typography>
        <input
          className="border rounded px-1.5 py-1"
          type="number"
          defaultValue={triggerInterval}
          min="0"
          max="300"
          onChange={(e) => {
            const value = +e.target.value;
            setTriggerInterval(value >= 1 ? value : 1);
          }}
          style={{ width: "7ch" }}
        />
        <Typography variant="detail" className="text-gray-900 ml-2">
          seconds
        </Typography>
      </Box>
    </Box>
  );

  const handleThresholdError = (value: boolean) => {
    setHasMobileThresholdError(value);
  };
  const onQueryRateOkay = () => {
    setIsQueryLimitModalOpen(false);
    navigate("/reef/my-account?service_plan_modal=true");
  };

  const [isResettingDetector, setIsResettingDetector] = useState(false);

  return (
    <Box id="explore-route">
      <DemoSubNavbar
        detector={detector}
        loadingPredictors={isLoadingDetector}
        setIsRestartModalOpen={setIsResetModalOpen}
        onDetectorSelect={onDetectorSelect}
        shouldShowSelector={hasExploreDetector && !isResettingDetector}
      />
      <ExpirePlanBar />
      <TerminationNoticeBar />
      {hasExploreDetector && !isResettingDetector ? (
        <Container>
          <Box className="flex items-center my-[10px]">
            <Box className="flex h-[30px] gap-2 items-center bg-white px-2 py-1 rounded shadow-md w-[calc(100%-40px)] md:w-full">
              <Typography textTransform="uppercase" sx={{ display: ["none", "block"] }}>
                Query:
              </Typography>
              <Typography variant="subtitle" fontWeight="bold" sx={{ color: "#0D297D" }} noWrap>
                {detector?.label_prompt}
              </Typography>
            </Box>

            <IconButton
              onClick={() => setIsResetModalOpen(true)}
              className="h-[32px] self-center bg-white bg-opacity-20 md:hidden"
            >
              <RestartAltIcon className="text-black" />
            </IconButton>
          </Box>
          <Box className="flex flex-col sm:flex-row justify-start gap-[20px]">
            {/* Camera and settings */}
            <Box
              id="demo-camera-settings"
              className="relative flex flex-row sm:inline-flex sm:flex-col w-full sm:w-1/3"
            >
              <Box className="w-[70%] sm:w-full">
                <SimpleWebcam ref={webcamRef} setDeviceId={setDeviceId} deviceId={deviceId} height={cameraHeight} />
              </Box>
              {/* Settings and capture button */}
              <Box className="w-[30%] sm:w-full flex flex-col">
                <Box className="w-full order-1 sm:order-2">
                  <DemoSettings detector={detector} handleHasThresholdError={handleThresholdError} />
                </Box>
                <Box
                  className={`${
                    deviceId && "sm:-mt-3"
                  } sm:bg-white mb-2 sm:mb-5 rounded w-full px-2 order-2 sm:order-1`}
                >
                  {/* this dividir and typography is only visible in mobile */}
                  <Divider sx={{ borderWidth: "1px", my: "0.5rem", width: "100", display: ["block", "none"] }} />
                  <Typography variant="detail" align="center" sx={{ display: ["block", "none"] }}>
                    Capture
                  </Typography>
                  <Box className="flex flex-col sm:flex-row justify-between items-center sm:border-b-0 border-solid border-gray-300">
                    <IconButton
                      onClick={() => setAutoTriggerPopOver(!autoTriggerPopOver)}
                      sx={{ color: autoTriggerPopOver ? "#0450BA" : "unset" }}
                    >
                      <DemoAutoTriggerIcon color="#49494d" />
                    </IconButton>
                    <Box className="flex flex-col sm:flex-row items-center">
                      {disabledCountdown > 0 && (
                        <Typography className="text-[#15121A] mr-0 sm:mr-2 -mt-2 mb-1 sm:mb-0 sm:mt-0">
                          Please wait: <span className="text-[#006DD9]">{`${disabledCountdown}s`}</span>
                        </Typography>
                      )}
                      <GroundlightButton
                        onClick={createPosicheck}
                        variant="filled"
                        disabled={snapDisabled || !detector || detector?.status === "OFF" || hasMobileThresholdError}
                        endIcon={Camera}
                      >
                        <Typography>{isMD ? "Capture Image" : ""}</Typography>
                      </GroundlightButton>
                    </Box>
                  </Box>

                  {isMD && autoTriggerPopOver && autoCaptureSection()}
                </Box>
              </Box>
              {!isMD && autoTriggerPopOver && (
                <>
                  <Divider
                    sx={{ borderWidth: "1px", my: "0.5rem", mb: "1rem", width: "100", display: ["block", "none"] }}
                  />
                  {autoCaptureSection()}
                  <Divider
                    sx={{ borderWidth: "1px", my: "0.5rem", mt: "1rem", width: "100", display: ["block", "none"] }}
                  />
                </>
              )}
            </Box>

            {/* Metrics and lists only displayed in desktop */}
            <Box id="demo-metrics" className="w-full sm:w-2/3">
              {detector && (
                <Box className="hidden sm:flex justify-end">
                  <Box className="flex gap-1">
                    <Typography fontWeight="semibold" textTransform="uppercase" className="text-black">
                      Cloud Labels:
                    </Typography>

                    <Typography fontWeight="semibold" textTransform="uppercase" className="text-gray-500">
                      {detector?.escalation_type === "STANDARD" ? "ENABLED" : "DISABLED"}
                    </Typography>
                  </Box>
                </Box>
              )}
              <SimpleDemoStats detector={detector} isResetModalOpen={isResetModalOpen} />
            </Box>
          </Box>
        </Container>
      ) : (
        <DemoPageOnboarding
          onSubmit={(labelPrompt) => {
            if (isResettingDetector) {
              onResetDetector(labelPrompt);
              setIsResettingDetector(false);
            } else {
              createNewExploreDetector(labelPrompt);
            }
          }}
          loading={isMutatingDetector}
          initialStep={isResettingDetector ? 2 : 0}
        />
      )}

      <ResetDemoDetectorModal
        open={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        onConfirmation={async () => {
          setIsResetModalOpen(false);
          setIsResettingDetector(true);

          if (detector) {
            await deleteDetector.mutateAsync(detector.id);

            // refetch user to make sure explore detector is in user info
            const updatedUser = await refetchUser();
            dispatch(setAuthUser(updatedUser));
          }
        }}
      />

      <ConfirmDeleteModal
        modalTitle="Query Rate Limit"
        modalContent="Free account query rate limit is 6 queries per minute. Upgrade to a paid plan to increase your query rate limit."
        onRemoveHandler={onQueryRateOkay}
        setIsDeleteModalOpen={setIsQueryLimitModalOpen}
        isDeleteModalOpen={isQueryLimitModalOpen}
        buttonTexts={{
          cancelText: "Close",
          successText: "See plans",
        }}
      />
      <UserLimitModal type="image-query" isModalOpen={modalLimit} handleCloseModal={handleCloseModalLimit} />
    </Box>
  );
}
